import { useEffect, useState, type FormEvent } from "react";
import styled from "@emotion/styled";
import { ArrowLeft, Info, Loader2, Plus, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useApi } from "@/hooks/useApi";
import {
  FINANCE_COMPANIES, PROPOSAL_DECISIONS, lenderDecisionService, type LenderDecision,
} from "@/services/LenderDecisionService";

const TableViewport = styled.div`
  overflow-x: auto;
  table { width: 100%; min-width: 600px; table-layout: fixed; border-collapse: collapse; font-size: 12px; }
  th, td { padding: 10px 8px; border-bottom: 1px solid var(--border); text-align: left; }
  th { width: calc(25% - 25px); font-weight: 600; }
  th:nth-of-type(3) { width: 100px; }
  td { overflow-wrap: anywhere; min-width: 100px; }
`;

const FormFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  label { display: flex; flex-direction: column; gap: 6px; min-width: 0; font-size: 12px; }
  select { width: 100%; min-width: 0; height: 36px; padding: 0 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--background); }
  select:disabled { opacity: 0.6; }
  @media (max-width: 640px) { grid-template-columns: minmax(0, 1fr); }
`;

const purpleOutline = "border-purple-600 bg-transparent text-purple-600 hover:bg-purple-50 hover:text-purple-700";

function DecisionsTable({ rows }: { rows: LenderDecision[] }) {
  return (
    <TableViewport>
      <table>
        <thead><tr>{["Last Updated", "Finance Company", "APR", "Decision", "Reference"].map((heading) => <th key={heading}>{heading}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.proposalId}-${index}`}>
              <td>{row.modifiedOn ? new Date(row.modifiedOn).toLocaleDateString("en-GB") : "—"}</td>
              <td>{row.lenderName}</td>
              <td>{row.aprRange || "—"}</td>
              <td>
                <span className={`flex items-center justify-center gap-1 rounded px-2 py-1 text-center ${
                  row.status === "Accepted" ? "bg-emerald-50 text-emerald-800" :
                  ["Declined", "Failed Send Rules", "Expired", "Non Qualifying"].includes(row.status) ? "bg-rose-50 text-rose-800" : "bg-amber-50 text-amber-800"
                }`}>
                  <span>{row.status}</span>
                  {row.fixedDeclinedReason && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" aria-label={`Decision information for ${row.lenderName}`} className="inline-flex shrink-0">
                          <Info className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-64 whitespace-normal break-words">{row.fixedDeclinedReason}</TooltipContent>
                    </Tooltip>
                  )}
                </span>
              </td>
              <td>{row.status === "Non Qualifying" ? "" : row.lenderReference || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableViewport>
  );
}

export function LenderDecisionHistoryDialog({
  applicationId, onOpenChange, canEdit, verifyClaimOwnership, onDecisionsChanged,
}: {
  applicationId: number;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
  verifyClaimOwnership: () => Promise<boolean>;
  onDecisionsChanged: () => void;
}) {
  const [view, setView] = useState<"history" | "proposal">("history");
  const [financeCompanyId, setFinanceCompanyId] = useState("");
  const [tierId, setTierId] = useState("");
  const [decisionId, setDecisionId] = useState("");
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [proposalSucceeded, setProposalSucceeded] = useState(false);
  const [confirmRescore, setConfirmRescore] = useState(false);
  const [rescoreSucceeded, setRescoreSucceeded] = useState(false);
  const [rescoreError, setRescoreError] = useState<string | null>(null);
  const storageKey = `rescoreAvailableAt_${applicationId}`;
  const [availableAt, setAvailableAt] = useState(() => {
    const stored = Date.parse(localStorage.getItem(storageKey) ?? "");
    return Number.isFinite(stored) && stored > Date.now() ? stored : 0;
  });
  useEffect(() => {
    if (!availableAt) return;
    const timer = window.setTimeout(() => {
      localStorage.removeItem(storageKey);
      setAvailableAt(0);
    }, Math.max(0, availableAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [availableAt, storageKey]);

  const history = useApi((signal) => lenderDecisionService.getHistory(applicationId, signal), [applicationId]);
  const tiers = useApi(
    (signal) => lenderDecisionService.getTiers(Number(financeCompanyId), signal),
    [financeCompanyId], { skip: !financeCompanyId }
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!FINANCE_COMPANIES.some((company) => company.id === Number(financeCompanyId)) ||
      !tiers.data?.some((tier) => tier.id === tierId) ||
      !PROPOSAL_DECISIONS.some((decision) => decision.id === Number(decisionId))) {
      setError("Select a finance company, tier and decision.");
      return;
    }
    const lenderReference = reference.trim();
    const hasControlCharacters = Array.from(lenderReference).some((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127;
    });
    if (!lenderReference || lenderReference.length > 100 || hasControlCharacters) {
      setError("Enter a reference of 1 to 100 characters without control characters.");
      return;
    }
    setBusy(true);
    try {
      if (!(await verifyClaimOwnership())) throw new Error("Claim this request before adding a proposal.");
      await lenderDecisionService.addProposal({ applicationId, financeCompanyId: Number(financeCompanyId), financeCompanyTierId: tierId, loanProposalStatusId: Number(decisionId), lenderReference });
      setProposalSucceeded(true);
      history.refetch();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not add proposal. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleRescore = async () => {
    setRescoreError(null);
    setBusy(true);
    try {
      if (!(await verifyClaimOwnership())) throw new Error("Claim this request before rescoring.");
      await lenderDecisionService.rescore(applicationId);
      const next = Date.now() + 10 * 60 * 1000;
      localStorage.setItem(storageKey, new Date(next).toISOString());
      setAvailableAt(next);
      setRescoreSucceeded(true);
      history.refetch();
    } catch (failure) {
      setRescoreError(failure instanceof Error ? failure.message : "Could not rescore. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!busy) onOpenChange(open); }}>
      <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden sm:max-w-[868px] lg:max-w-[868px]" style={{ maxWidth: "min(868px, calc(100vw - 2rem))" }} onEscapeKeyDown={(event) => { if (busy) event.preventDefault(); }} onPointerDownOutside={(event) => { if (busy) event.preventDefault(); }}>
        <DialogHeader className="shrink-0">
          <div className="flex items-center justify-between gap-2 pr-8">
            <DialogTitle className="text-base text-purple-600">{view === "history" ? "Decision History" : "Add proposal"}</DialogTitle>
            {view === "history" && (
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="outline" size="sm" className={purpleOutline} aria-label="Refresh decisions" title="Refresh decisions" disabled={busy || history.loading} onClick={history.refetch}><RefreshCw className={`size-4 ${history.loading ? "animate-spin" : ""}`} />Refresh</Button>
                <Button variant="outline" size="sm" className={purpleOutline} disabled={!canEdit || busy || !!availableAt} title={availableAt ? `Rescore available at ${new Date(availableAt).toLocaleTimeString("en-GB")}` : !canEdit ? "Claim this request to rescore" : undefined} onClick={() => setConfirmRescore(true)}><RefreshCw className="size-4" />Re-score</Button>
              </div>
            )}
          </div>
          <DialogDescription className="sr-only">Lender decisions for application {applicationId}</DialogDescription>
        </DialogHeader>
        {error && <p role="alert" className="shrink-0 text-sm text-rose-600">{error}</p>}
        {view === "history" ? (
          <>
            <div role="region" aria-label="Decision history entries" className="flex min-h-0 flex-col gap-4 overflow-y-auto">
            {history.loading && <p role="status" className="flex items-center gap-2 text-sm"><Loader2 className="size-4 animate-spin" />Loading decisions...</p>}
            {history.error && <p role="alert" className="text-sm text-rose-600">Could not load decisions: {history.error}</p>}
            {!history.loading && !history.error && !history.data?.length && <p className="text-sm text-muted-foreground">No lender decision history for application {applicationId}.</p>}
            {!!history.data?.length && <>
              <section aria-label="Best Product"><h3 className="mb-2 text-sm font-semibold text-purple-600">Best Product</h3><DecisionsTable rows={history.data.slice(0, 1)} /></section>
              {history.data.length > 1 && <section aria-label="Other Products"><h3 className="mb-2 text-sm font-semibold text-purple-600">Other Products</h3><DecisionsTable rows={history.data.slice(1)} /></section>}
            </>}
            </div>
            <div className="flex shrink-0 justify-end border-t border-border pt-4"><Button variant="outline" className={purpleOutline} disabled={!canEdit || busy} title={!canEdit ? "Claim this request to add a proposal" : undefined} onClick={() => { setError(null); setConfirmRescore(false); setView("proposal"); }}><Plus className="size-4" />Add proposal</Button></div>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="flex min-h-0 flex-col gap-4 overflow-y-auto">
            <FormFields>
              <label>Finance Company<select value={financeCompanyId} disabled={busy} onChange={(event) => { setFinanceCompanyId(event.target.value); setTierId(""); setError(null); }} required><option value="">Please select</option>{FINANCE_COMPANIES.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}</select></label>
              <label>Tier<select value={tierId} onChange={(event) => setTierId(event.target.value)} disabled={busy || !financeCompanyId || tiers.loading || !!tiers.error} required><option value="">{tiers.loading ? "Loading tiers..." : "Please select"}</option>{(!tiers.loading && !tiers.error ? tiers.data ?? [] : []).map((tier) => <option key={tier.id} value={tier.id}>{tier.name}</option>)}</select></label>
              <label>Decision<select value={decisionId} disabled={busy} onChange={(event) => setDecisionId(event.target.value)} required><option value="">Please select</option>{PROPOSAL_DECISIONS.map((decision) => <option key={decision.id} value={decision.id}>{decision.name}</option>)}</select></label>
              <label>Finance Company Reference<Input value={reference} disabled={busy} maxLength={100} required onChange={(event) => setReference(event.target.value)} /></label>
            </FormFields>
            {financeCompanyId && tiers.error && <p role="alert" className="text-sm text-rose-600">{tiers.error}<Button type="button" variant="ghost" size="sm" aria-label="Retry tiers" title="Retry tiers" onClick={tiers.refetch}><RefreshCw className="size-4" /></Button></p>}
            <div className="flex flex-wrap justify-between gap-3"><Button type="button" variant="outline" disabled={busy} onClick={() => { setError(null); setView("history"); }}><ArrowLeft className="size-4" />Back</Button><Button type="submit" variant="outline" className={purpleOutline} disabled={!canEdit || busy || tiers.loading}><Plus className="size-4" />{busy ? "Submitting..." : "Submit proposal"}</Button></div>
          </form>
        )}
      </DialogContent>
      <Dialog open={proposalSucceeded} onOpenChange={(open) => {
        if (!busy && !proposalSucceeded) setProposalSucceeded(open);
      }}>
        <DialogContent
          className="sm:max-w-md lg:max-w-md"
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Proposal added</DialogTitle>
            <DialogDescription>Proposal added successfully. It will be available in your application in a few minutes</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className={purpleOutline} onClick={() => {
              setProposalSucceeded(false);
              onOpenChange(false);
              onDecisionsChanged();
            }}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmRescore} onOpenChange={(open) => {
        if (!busy && !rescoreSucceeded) {
          setConfirmRescore(open);
          if (!open) setRescoreError(null);
        }
      }}>
        <DialogContent
          className="sm:max-w-md lg:max-w-md"
          showCloseButton={!busy && !rescoreSucceeded}
          onEscapeKeyDown={(event) => { if (busy || rescoreSucceeded) event.preventDefault(); }}
          onPointerDownOutside={(event) => { if (busy || rescoreSucceeded) event.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>{rescoreSucceeded ? "Application rescored" : "Re-score application"}</DialogTitle>
            <DialogDescription className={rescoreSucceeded ? "text-emerald-700" : undefined}>
              {rescoreSucceeded ? "Application has been rescored." : "Are you sure you want to re-score this application?"}
            </DialogDescription>
          </DialogHeader>
          {rescoreError && <p role="alert" className="text-sm text-rose-600">{rescoreError}</p>}
          <DialogFooter>
            {rescoreSucceeded ? (
              <Button onClick={() => {
                setConfirmRescore(false);
                onOpenChange(false);
                onDecisionsChanged();
              }}>OK</Button>
            ) : (
              <>
                <Button variant="outline" disabled={busy} onClick={() => {
                  setConfirmRescore(false);
                  setRescoreError(null);
                }}>Cancel</Button>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white" disabled={busy} onClick={handleRescore}>
                  {busy && <Loader2 className="size-4 animate-spin" />}
                  Confirm re-score
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}